import { getAvailableColumns, getVisibleColumns } from '../voc-items.utils';
import { PerSubscriptionUserStorage } from '@utils/storage';
import isEqual from 'lodash/isEqual';
import get from 'lodash/get';

const state = {
    visibleColumns: [],
    columnWidths: null,
    type: '',
    sort: {
        prop: 'lastUpdatedAt',
        order: 'descending',
    },
    defaultSort: {
        prop: 'lastUpdatedAt',
        order: 'descending',
    },
};

const mutations = {
    setVisibleColumns (state, { visibleColumns }) {
        state.visibleColumns = visibleColumns || [];
    },
    setColumnWidths (state, { columnWidths }) {
        state.columnWidths = columnWidths;
    },
    setSort (state, { sort }) {
        state.sort = sort;
    },
    setType (state, { type }) {
        state.type = type;
    },
};

const actions = {
    async loadAll ({ state, commit }, { type }) {
        commit('setType', { type });

        let storedColumns = state.visibleColumns.length && state.type === type ? state.visibleColumns : null;
        if (type === 'idea') {
            const [sortConfig, columnConfig, columnWidthsConfig] = await Promise.all([
                PerSubscriptionUserStorage.get('vocListSort'),
                PerSubscriptionUserStorage.get('vocListColumns'),
                PerSubscriptionUserStorage.get('vocListColumnWidths'),
            ]);
            storedColumns = get(columnConfig, 'value');

            commit('setColumnWidths', { columnWidths: get(columnWidthsConfig, 'value') || null });

            if (get(sortConfig, 'value')) {
                commit('setSort', { sort: get(sortConfig, 'value') });
            }
        }
        const visibleColumns = getVisibleColumns({ type, storedColumns });
        commit('setVisibleColumns', { visibleColumns });
    },
    async update ({ state, commit }, { sort, columns, columnWidths, type, columnWidthsAppend = false }) {
        if (sort !== undefined && sort !== state.sort) {
            if (!sort) sort = state.defaultSort;

            commit('setSort', { sort });

            if (type === 'idea') {
                if (isEqual(sort, state.defaultSort) && PerSubscriptionUserStorage.get('vocListSort')) {
                    PerSubscriptionUserStorage.remove('vocListSort');
                } else if (!isEqual(sort, PerSubscriptionUserStorage.get('vocListSort'))) {
                    PerSubscriptionUserStorage.set('vocListSort', sort);
                }
            }
        }
        if (columns !== undefined && columns !== state.visibleColumns) {
            if (!columns) {
                columns = getVisibleColumns({ type: state.type, storedColumns: null });
            }
            commit('setVisibleColumns', { visibleColumns: columns });

            if (type === 'idea') {
                if (isEqual(columns, getVisibleColumns({ type: state.type, storedColumns: null }))) {
                    PerSubscriptionUserStorage.remove('vocListColumns');
                } else {
                    PerSubscriptionUserStorage.set('vocListColumns', JSON.stringify(columns));
                }
            }
        }
        if (columnWidths !== undefined) {
            let newColumnWidths = null;

            if (columnWidths && columnWidthsAppend) {
                newColumnWidths = {
                    ...state.columnWidths,
                    ...columnWidths,
                };
            } else {
                newColumnWidths = columnWidths;
            }

            if (state.columnWidths !== newColumnWidths) {
                commit('setColumnWidths', { columnWidths: newColumnWidths });
                if (type === 'idea') {
                    if (newColumnWidths) {
                        PerSubscriptionUserStorage.set('vocListColumnWidths', JSON.stringify(newColumnWidths));
                    } else {
                        PerSubscriptionUserStorage.remove('vocListColumnWidths');
                    }
                }
            }
        }
    },
};

const getters = {
    availableColumns: (state, _getters, rootState, rootGetters) => {
        const { settings } = rootState.subscription;
        const revenueLabel = !get(settings, 'revenue.label') ? 'Revenue' : get(settings, 'revenue.label', 'Revenue');
        const availableColumns = getAvailableColumns({
            type: state.type,
            hasSegmentFlag: rootGetters['auth/hasSegmentFlag'],
            isMultiApp: rootGetters['apps/isMultiApp'],
            revenueLabel,
            getFeatureFlag: rootGetters['subscription/getFeatureFlag'],
            isLoadedSegmentFlags: rootGetters['auth/isLoadedSegmentFlags'],
            hasProductLinePurchased: rootGetters['productLines/hasProductLinePurchased'],
        });

        return availableColumns;
    },
    columns: (state, getters) => {
        const visibleColumns = new Array(state.visibleColumns.length);
        const hiddenColumns = [];

        getters.availableColumns.forEach((column) => {
            const indexOfColumn = state.visibleColumns.indexOf(column.prop);
            const savedColumnWidth = get(state.columnWidths, `['${column.prop}']`) || column.width;

            if (indexOfColumn !== -1) {
                visibleColumns[indexOfColumn] = {
                    ...column,
                    visible: true,
                    ...(savedColumnWidth && { width: savedColumnWidth }),
                };
            } else {
                hiddenColumns.push({
                    ...column,
                    visible: false,
                    ...(savedColumnWidth && { width: savedColumnWidth }),
                });
            }
        });

        return [...visibleColumns.filter((column) => !!column), ...hiddenColumns];
    },
    tableConfig: (state) => ({
        ...(state.sort && { sort: state.sort }),
        ...(state.visibleColumns && state.visibleColumns.length && { columns: state.visibleColumns }),
        ...(state.columnWidths && { columnWidths: state.columnWidths }),
    }),
    defaultTableConfig: (state) => ({
        sort: state.defaultSort,
        columns: getVisibleColumns({ type: state.type, storedColumns: null }),
        columnWidths: null,
    }),
    emptyTableConfig: (state) => ({
        sort: state.defaultSort,
        columns: getVisibleColumns({ type: state.type, storedColumns: [] }),
    }),
    feedbackEmptyTableConfig: (state) => ({
        sort: state.defaultSort,
        columns: getVisibleColumns({ type: 'feedback', storedColumns: [] }),
    }),
};

export default {
    namespaced: true,
    state,
    getters,
    mutations,
    actions,
};
